<template>
	<div class="users-info" :class="[editionMode ? 'users-info--edit' : '']">
		<BlockNotifications
			v-if="showNotificationError"
			:title="'User Creation Error'"
			:message="
				`Unable to ${
					editionMode ? 'update' : 'create'
				} the user due to internal Error. Please try again or contact support to resolve the error. ${
					errorMessage ? errorMessage : ''
				}`
			"
			:blockState="'error'"
			ref="blockErrorContainer"
		/>
		<div class="users-info__info-container">
			<div class="users-info__row">
				<div class="users-info__first-name">
					<Input
						ariaLabel="Aria Label"
						className="field__input"
						ref="firstName"
						label="First Name"
						name="firstName"
						:required="true"
						v-model="user.firstName"
						:forceCustomErrorMessage="firstNameCustomErrorMessage"
						:maxLength="50"
						@input="setFirstName"
						data-test="user-form-firstName"
					/>
				</div>
				<div class="users-info__last-name">
					<Input
						ariaLabel="Aria Label"
						className="field__input"
						label="Last Name"
						name="lastName"
						:required="true"
						v-model="user.lastName"
						:forceCustomErrorMessage="lastNameCustomErrorMessage"
						:maxLength="50"
						@input="setLastName"
					/>
				</div>
			</div>
			<div
				class="users-info__row"
				:class="[editionMode ? 'users-info__row--edit' : '']"
			>
				<div class="users-info__email">
					<div class="users-info__email-static" v-if="editionMode">
						<Ellipsis
							:data="user.email"
							class="users-info__email-static-email"
							:position="'top'"
						>
							<template #ellipsis-label>
								<span class="users-info__email-static-label"
									>Email:
								</span></template
							></Ellipsis
						>
					</div>

					<Input
						ariaLabel="Aria Label"
						className="field__input"
						inputType="Email"
						label="Email"
						name="email"
						placeholder="example@mail.com"
						:helpText="'Email must be unique'"
						:required="true"
						v-model="user.email"
						:maxLength="100"
						:forceCustomErrorMessage="
							emailCustomErrorMessage || forceErrorMessage
						"
						@input="setEmail"
						v-else
					/>
				</div>
				<div class="users-info__status">
					<TooltipContainer
						:tooltip="
							isDisableActiveDeactiveUser
								? 'Unable to deactivate user. User is the current user.'
								: ''
						"
						:position="'bottom-end'"
					>
						<template #container>
							<Toggle
								:label="user.active ? 'Active' : 'Inactive'"
								v-model="user.active"
								:disabled="isDisableActiveDeactiveUser"
							/>
						</template>
					</TooltipContainer>
				</div>
			</div>
		</div>
		<div class="users-info__role-container">
			<Heading
				:title="'User Type Selection'"
				:subHeadingText="'Choose at least one role'"
				:variant="'medium'"
				:showIcone="false"
			/>
			<div class="users-info__role-internal">
				<Heading
					:title="'Internal'"
					:variant="'small'"
					:showIcone="false"
				/>
				<RowSet :labelText="'Roles'">
					<template #rowset-content>
						<DropdownMultiselectV2
							:options="internalRoleOptions"
							:timeout="2000"
							placeholder="Select one or more"
							searchPlaceholder="Search"
							v-model="user.internalIds"
							:width="370"
						/>
					</template>
				</RowSet>
			</div>
			<div class="users-info__role-consultant">
				<Heading
					:title="'Consultant'"
					:variant="'small'"
					:showIcone="false"
				/>
				<RowSet :labelText="'Roles'">
					<template #rowset-content>
						<DropdownMultiselectV2
							:options="consultantRoleOptions"
							:timeout="2000"
							placeholder="Select one or more"
							searchPlaceholder="Search"
							v-model="user.consultantIds"
							:width="370"
						/>
					</template>
				</RowSet>
				<RowSet :labelText="'Title'">
					<template #rowset-content>
						<DropdownV2
							placeholder="Select"
							:displayHelp="false"
							:options="titleOptions"
							v-model="user.title"
							:disabled="isTitleDropdownDisabled"
						/>
					</template>
				</RowSet>
			</div>
		</div>
	</div>
</template>
<script>
import Input from '@/components/forms/input/input';
import Toggle from '@/components/forms/selection-control/toggle/toggle';
import DropdownV2 from '@/components/forms/dropdown/dropdown-v2.vue';
import DropdownMultiselectV2 from '@/components/forms/dropdown/dropdown-multiselect-v2.vue';
import Heading from '@/components/visual/heading.vue';
import RowSet from '@/components/shells/rowset/rowset.vue';
import BlockNotifications from '@/components/notifications/block-notifications/block-notifications';
import TooltipContainer from '@/components/shells/tooltip/tooltip-container.vue';
import { required, email, maxLength } from 'vuelidate/lib/validators';
import { mapState, mapActions } from 'vuex';
import {
	MODULE_NAME as OPTIONS_MODULE_NAME,
	INTERNAL_ROLES_OPTS,
	CONSULTANT_ROLES_OPTS
} from '@/sections/options/options-mutation-type';
import Ellipsis from '@/components/visual/ellipsis';

import {
	SUPPORT_EXTERNAL,
	SUPPORT_PORTAL
} from '@/sections/users/shared/users-const.js';
import _ from 'lodash';

export default {
	name: 'UserForm',
	model: {
		prop: 'user'
	},
	components: {
		Input,
		Toggle,
		DropdownV2,
		DropdownMultiselectV2,
		Heading,
		BlockNotifications,
		RowSet,
		Ellipsis,
		TooltipContainer
	},
	props: {
		user: {
			type: Object,
			default: () => {}
		},
		isDisabled: {
			type: Boolean,
			default: false
		},
		errorMessage: {
			type: String,
			default: ''
		},
		forceErrorMessage: {
			type: String,
			default: ''
		},
		showNotificationError: {
			type: Boolean,
			default: false
		}
	},
	validations: {
		user: {
			firstName: {
				required,
				maxLength: maxLength(50)
			},
			lastName: {
				required,
				maxLength: maxLength(50)
			},
			email: {
				required,
				email,
				maxLength: maxLength(100)
			},
			internalIds: {
				required(value) {
					return (
						this.user.consultantIds.length > 0 || value.length > 0
					);
				}
			},
			consultantIds: {
				required(value) {
					return this.user.internalIds.length > 0 || value.length > 0;
				}
			}
		}
	},
	data() {
		return {
			titleOptions: [
				{ text: 'Operations', value: 'Operations' },
				{ text: 'Install', value: 'Install' },
				{ text: 'IT', value: 'IT' },
				{ text: 'Fixed Ops', value: 'Fixed Ops' }
			],
			consultantExternalParams: {
				page: 1,
				count: 1000,
				sortdirection: 'asc',
				active: true,
				clientId: SUPPORT_EXTERNAL
			},
			supportPortalParams: {
				page: 1,
				count: 1000,
				sortdirection: 'asc',
				active: true,
				clientId: SUPPORT_PORTAL
			}
		};
	},
	async created() {
		await this.getRolesOptions(this.supportPortalParams);

		await this.getRolesOptions(this.consultantExternalParams);
	},
	computed: {
		...mapState(OPTIONS_MODULE_NAME, [
			INTERNAL_ROLES_OPTS,
			CONSULTANT_ROLES_OPTS
		]),

		optionRoles() {
			return this.$store.getters.localStorage.optionRoles;
		},
		firstNameCustomErrorMessage() {
			if (this.$v.user.firstName.$dirty) {
				if (!this.$v.user.firstName.required) {
					return 'First Name is required';
				} else if (!this.$v.user.firstName.maxLength) {
					return `First Name must have less than ${this.$v.user.firstName.$params.maxLength.max} letters`;
				} else {
					return '';
				}
			} else {
				return '';
			}
		},
		lastNameCustomErrorMessage() {
			if (this.$v.user.lastName.$dirty) {
				if (!this.$v.user.lastName.required) {
					return 'Last Name is required';
				} else if (!this.$v.user.lastName.maxLength) {
					return `Last Name must have less than ${this.$v.user.lastName.$params.maxLength.max} letters`;
				} else {
					return '';
				}
			} else {
				return '';
			}
		},
		emailCustomErrorMessage() {
			if (this.$v.user.email.$dirty) {
				if (!this.$v.user.email.email) {
					return 'Email format invalid';
				} else {
					return '';
				}
			} else {
				return '';
			}
		},
		internalRoleOptions() {
			const internalRoles = _.get(this[INTERNAL_ROLES_OPTS], 'items', []);
			if (!_.isEmpty(internalRoles)) {
				return this.mapOptionsByTextandvalue(internalRoles);
			}
			return internalRoles;
		},
		consultantRoleOptions() {
			const consultantRoles = _.get(
				this[CONSULTANT_ROLES_OPTS],
				'items',
				[]
			);
			if (!_.isEmpty(consultantRoles)) {
				return this.mapOptionsByTextandvalue(consultantRoles);
			}
			return consultantRoles;
		},
		isTitleDropdownDisabled() {
			return this.user && this.user.consultantIds.length === 0;
		},
		hasError() {
			return this.$v.$invalid;
		},
		editionMode() {
			return !!(this.user && this.user.userId);
		},
		isDisableActiveDeactiveUser() {
			if (!this.editionMode) return false;
			const user = this.$store.getters['oidcStore/oidcUser'];
			const { Email } = user;
			return this.user.email === Email;
		}
	},
	methods: {
		...mapActions('options', ['getRolesOptions']),
		setFirstName(value) {
			this.user.firstName = value;
			this.$v.user.firstName.$touch();
			this.$emit('input', this.user);
		},
		setLastName(value) {
			this.user.lastName = value;
			this.$v.user.lastName.$touch();
			this.$emit('input', this.user);
		},
		setEmail(value) {
			this.user.email = value;
			this.$v.user.email.$touch();
			this.$emit('input', this.user);
		},
		mapOptionsByTextandvalue(list) {
			return list.map(option => {
				return {
					text: option.text,
					value: parseInt(option.value)
				};
			});
		}
	},
	watch: {
		hasError: {
			immediate: true,
			handler(val) {
				this.$emit('hasError', val);
			}
		},
		isTitleDropdownDisabled: {
			handler(val) {
				if (val) {
					this.user.title = null;
				}
			}
		},
		forceErrorMessage: {
			handler(val) {
				if (val) {
					this.$emit('hasError', true);
				} else {
					this.$emit('hasError', this.hasError);
				}
			}
		}
	}
};
</script>

<style lang="scss">
/**
    * [Description]
    *
    * User Form
    * Can use SCSS variables = TRUE
    *
 */

/*------------------------------------*\
  #BASE
\*------------------------------------*/
.users-info {
	display: flex;
	flex-direction: column;
	gap: $gutter-xl;

	&__info-container {
		display: flex;
		flex-direction: column;
		gap: $gutter-lg;

		.field__input-container {
			width: 275px;
		}
	}

	&__row {
		display: flex;
		gap: $gutter-lg;
	}

	&__status {
		margin-top: $gutter-xl;
	}

	&__role-container .dropdown {
		width: 370px;
	}

	&__role-internal {
		margin-top: $gutter-lg;
		display: flex;
		flex-direction: column;
		gap: $gutter-lg;
	}

	&__role-consultant {
		margin-top: $gutter-xxl;
		display: flex;
		flex-direction: column;
		gap: $gutter-lg;
	}

	&__email-static {
		&-label {
			color: $neutral-2;
			font-size: $label-font-size;
			margin-right: $gutter-xs;
		}

		&-email {
			align-items: center;
		}
	}
}
.users-info--edit {
	.users-info {
		&__status {
			margin-top: 0;
		}

		&__row--edit {
			justify-content: space-between;
			align-items: center;
		}

		@include mq($from: md) {
			&__info-container .field__input-container {
				width: 198px;
			}

			&__role-container .dropdown {
				width: 300px;
			}

			&__email-static {
				width: 275px;
			}
		}
		@include mq($from: lg) {
			&__info-container .field__input-container {
				width: 366px;
			}

			&__role-container .dropdown {
				width: 370px;
			}

			&__email-static {
				width: 630px;
			}
		}
	}
}
</style>
